/**
 * Prize card with opt-in 3D tilt (§13.2): follows the pointer (and device motion when the app grants it), is
 * disabled in calm mode, and never carries information — the text below the image does (§13.3 Imagery).
 * `layoutId` enables the shared-element transition into the storefront (§6.3).
 */
import { motion } from 'motion/react';
import { useCallback, useRef, type PointerEvent, type ReactNode } from 'react';
import { cn } from '../cn';
import { useEnergy } from '../motion/MotionProvider';
import { sharedElementTransition } from '../motion/presets';

export interface PrizeCardProps {
  title: string;
  imageUrl: string;
  imageAlt: string;
  /** Blur placeholder (tiny data URL) shown until the image loads. */
  placeholderUrl?: string;
  /** Per-prize accent colour (already contrast-checked by applyTheme; used only for the accent bar). */
  accent?: string;
  /** Textual value / description line — a prize image never holds the only copy of value (§13.3). */
  valueText?: string;
  badge?: ReactNode;
  tilt?: boolean;
  maxTiltDeg?: number;
  /** Shared-element id, e.g. `prize-${prizeId}`. */
  layoutId?: string;
  onClick?: () => void;
  children?: ReactNode;
  className?: string;
}

export function PrizeCard({
  title,
  imageUrl,
  imageAlt,
  placeholderUrl,
  accent,
  valueText,
  badge,
  tilt = true,
  maxTiltDeg = 8,
  layoutId,
  onClick,
  children,
  className,
}: PrizeCardProps) {
  const { energy, calm } = useEnergy();
  const ref = useRef<HTMLDivElement>(null);
  const active = tilt && !calm;

  const onMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      const el = ref.current;
      if (!el || !active || e.pointerType === 'touch') return;
      const r = el.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width - 0.5;
      const py = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty('--wd-tilt-y', `${(px * maxTiltDeg * 2).toFixed(2)}deg`);
      el.style.setProperty('--wd-tilt-x', `${(-py * maxTiltDeg * 2).toFixed(2)}deg`);
      el.style.setProperty('--wd-glare-x', `${((px + 0.5) * 100).toFixed(1)}%`);
      el.style.setProperty('--wd-glare-y', `${((py + 0.5) * 100).toFixed(1)}%`);
      el.style.setProperty('--wd-glare', '1');
    },
    [active, maxTiltDeg],
  );
  const reset = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty('--wd-tilt-x', '0deg');
    el.style.setProperty('--wd-tilt-y', '0deg');
    el.style.setProperty('--wd-glare', '0');
  }, []);

  const Tag = onClick ? 'button' : 'div';
  return (
    <motion.div
      ref={ref}
      layoutId={layoutId}
      transition={sharedElementTransition[energy]}
      className={cn('wd-prize-card', className)}
      data-tilt={active ? 'true' : 'false'}
      onPointerMove={onMove}
      onPointerLeave={reset}
      style={accent ? ({ '--wd-prize-accent': accent } as React.CSSProperties) : undefined}
    >
      <Tag
        type={onClick ? 'button' : undefined}
        onClick={onClick}
        style={{ all: 'unset', display: 'block', cursor: onClick ? 'pointer' : undefined, width: '100%' }}
      >
        <div style={{ position: 'relative' }}>
          {placeholderUrl ? (
            <img className="wd-gallery__placeholder" src={placeholderUrl} alt="" aria-hidden="true" />
          ) : null}
          <img
            className="wd-prize-card__img"
            src={imageUrl}
            alt={imageAlt}
            loading="lazy"
            decoding="async"
            style={{ position: 'relative' }}
          />
        </div>
        <div className="wd-prize-card__body">
          {badge}
          <h3 className="wd-prize-card__title">{title}</h3>
          {valueText ? (
            <p className="wd-muted" style={{ margin: 0 }}>
              {valueText}
            </p>
          ) : null}
          {children}
        </div>
      </Tag>
    </motion.div>
  );
}
